import { message } from 'ant-design-vue'
import request, { IPage, IWorkspaceResponse, IListWorkspaceResponse } from '/@/api/http/request'
import { TaskType, TaskStatus, OutOfControlAction } from '/@/types/task'
import { WaylineType } from '/@/types/wayline'

const HTTP_PREFIX = '/wayline/api/v1'

// Get Wayline Files
export const getWaylineFiles = async function (wid: string, body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${wid}/waylines?order_by=${body.order_by}&page=${body.page}&page_size=${body.page_size}&folder_id=${body.folder_id}`
  const result = await request.get(url)
  return result.data
}

// Download Wayline File
export const downloadWaylineFile = async function (workspaceId: string, waylineId: string): Promise<any> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/${waylineId}/url`
  const result = await request.get(url, { responseType: 'blob' })
  if (result.data.type === 'application/json') {
    const reader = new FileReader()
    reader.onload = function (e) {
      const text = reader.result as string
      const result = JSON.parse(text)
      message.error(result.message)
    }
    reader.readAsText(result.data, 'utf-8')
  } else {
    return result.data
  }
}

// Delete Wayline File
export const deleteWaylineFile = async function (workspaceId: string, waylineId: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/${waylineId}`
  const result = await request.delete(url)
  return result.data
}

export interface CreatePlan {
  name: string,
  file_id: string,
  dock_sn: string,
  task_type: TaskType, // 任务类型
  wayline_type: WaylineType, // 航线类型
  task_days: number[] // 执行任务的日期（秒）
  task_periods: number[][] // 执行任务的时间点（秒）
  rth_altitude: number // 相对机场返航高度 20 - 500
  job_type: string // 场景
  out_of_control_action: OutOfControlAction // 失控动作
  min_battery_capacity?: number, // The minimum battery capacity of aircraft.
  min_storage_capacity?: number, // The minimum storage capacity of dock and aircraft.
  is_analysis: boolean
  generate_report: number
  pilot_id: string
  pilot_name: string
  portion: string
  portion_code: string
  portion_type: string
}

// Create Wayline Job
export const createPlan = async function (workspaceId: string, plan: CreatePlan): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/flight-tasks`
  const result = await request.post(url, plan)
  return result.data
}

export interface Task {
  job_id: string,
  job_name: string,
  task_type: TaskType, // 任务类型
  file_id: string, // 航线文件id
  file_name: string, // 航线名称
  wayline_type: WaylineType, // 航线类型
  dock_sn: string,
  dock_name: string,
  workspace_id: string,
  username: string,
  begin_time: string,
  end_time: string,
  execute_time: string,
  completed_time: string,
  status: TaskStatus, // 任务状态
  progress: number, // 执行进度
  code: number, // 错误码
  rth_altitude: number // 相对机场返航高度 20 - 500
  out_of_control_action: OutOfControlAction // 失控动作
  out_of_control: OutOfControlAction // 失控动作
  media_count: number // 媒体数量
  uploading:boolean // 是否正在上传媒体
  uploaded_count: number // 已上传媒体数量
}

// Get Wayline Jobs
export const getWaylineJobs = async function (workspaceId: string, page: IPage): Promise<IListWorkspaceResponse<Task>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs?page=${page.page}&page_size=${page.page_size}`
  const result = await request.get(url)
  return result.data
}

export interface WaylineQueryParam {
  status: string // 任务状态
  task_type: string // 任务类型
  job_name: string // 任务名称
  dock_sn: string // 机场SN
  start_time: string // 开始时间
  end_time: string // 结束时间
  alarm_count: number
}

export interface WaylinePageRequest {
  page: number
  page_size: number
  param: WaylineQueryParam
}

export const getWaylineTask = async function (body: WaylinePageRequest): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/page/jobs`
  const result = await request.post(url, body)
  return result.data
}

export const getWaylineTaskList = async function (jobId): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/jobs/details/${jobId}`
  const result = await request.get(url)
  return result.data
}

export interface DeleteTaskParams {
  job_id: string
}

//  删除机场任务
export async function deleteTask (workspaceId: string, params: DeleteTaskParams): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs`
  const result = await request.delete(url, {
    params: params
  })
  return result.data
}

export enum UpdateTaskStatus {
  Suspend = 0, // 暂停
  Resume = 1, // 恢复
}
export interface UpdateTaskStatusBody {
  job_id: string
  status: UpdateTaskStatus
}

// 更新机场任务状态
export async function updateTaskStatus (workspaceId: string, body: UpdateTaskStatusBody): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs/${body.job_id}`
  const result = await request.put(url, {
    status: body.status
  })
  return result.data
}

// Upload Wayline file
export const importKmzFile = async function (workspaceId: string, file: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/file/upload`
  const result = await request.post(url, file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  })
  return result.data
}

// 媒体立即上传
export const uploadMediaFileNow = async function (workspaceId: string, jobId: string): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs/${jobId}/media-highest`
  const result = await request.post(url)
  return result.data
}

export const downloadZip = async function (workspaceId: string, jobId: string) {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/export-job-zip/${jobId}`
  const response = await request.get(url, { responseType: 'blob' })
  return response
}

export const downloadPdf = async function (workspaceId: string, jobId: string) {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/export-job-pdf/${jobId}`
  const response = await request.get(url, { responseType: 'blob' })
  return response
}

// 监测报告
export const downMonPdf = async function (workspace_id: string, job_id: string) {
  const url = `${HTTP_PREFIX}/workspaces/${workspace_id}/export-job-alarm-pdf/${job_id}`
  const response = await request.get(url, { responseType: 'blob' })
  return response
}

// 监测日报
export const downDayMonPdf = async function (workspace_id: string, date: string) {
  const url = `${HTTP_PREFIX}/workspaces/${workspace_id}/export-date-alarm-pdf/${date}`
  const response = await request.get(url, { responseType: 'blob' })
  return response
}

// 任务日报
export const downDayPdf = async function (workspace_id: string, date: string) {
  const url = `${HTTP_PREFIX}/workspaces/${workspace_id}/export-date-pdf/${date}`
  const response = await request.get(url, { responseType: 'blob' })
  return response
}

export interface lineSaveForm {
  id?:number,
  name:string,
  favorited?: boolean,
  wayline_id?:string,
  wayline_property:string,
  drone_model_key:string,
  drone_type?:string,
  payload_types?:string|string[],
  payload_model_keys: string[],
  template_types: number[],
  device_sn_list?: string[],
  user_id_list?:string[],
  user_names?:string
}

/** 保存或修改航线基本信息 */
export const saveLine = async (workspace_id:string, data:lineSaveForm) => {
  const url = `${HTTP_PREFIX}/workspaces/${workspace_id}/waylines/file/save-or-update-base-info`
  const response = await request.post(url, data)
  return response
}

export interface checkSaveName{
  name:string,
  wayline_id?:string
}
/** 检查航线名称唯一性 */
export const checkName = async (workspace_id:string, data:checkSaveName) => {
  const url = `${HTTP_PREFIX}/workspaces/${workspace_id}/waylines/file/check-unique-name`
  const response = await request.post(url, null, { params: data })
  return response
}
/** 导入航线kmz文件-覆盖重名文件 */
export const uploadReName = async (workspace_id:string, data:FormData) => {
  const url = `${HTTP_PREFIX}/workspaces/${workspace_id}/waylines/file/upload-for-cover`
  const response = await request.post(url, data)
  return response
}

export interface uploadCallbackData
  {
  name: string,
  metadata: {
    drone_model_key: string,
    payload_model_keys: string[],
    template_types: string[]
  },
  object_key: string

}

/**
* 上传回调接口
*
* @param workspace_id 工作区ID
* @param data 上传回调数据
* @returns 上传回调的响应
*/
export const uploadCallbck = async (workspace_id:string, data:uploadCallbackData) => {
  const url = `${HTTP_PREFIX}/workspaces/${workspace_id}/upload-callback`
  const response = await request.post(url, data)
  return response
}

/* interface DataVerificationResponse {
  code: number;
  message: string;
  data: dataVerification;
} */

export interface dataVerification {
  job_count: number;
  alarm_count: number;
}

export const dataVer = async function (workspace_id: string, date: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspace_id}/export-data-check/${date}`
  const response = await request.get(url)
  return response.data
}

export interface taskFightPath {
  job_id: string
}

// 查询航线任务的飞行轨迹点数据经纬度
export const taskTrajectory = async function (jobId: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/jobs/flight-path/points/${jobId}`
  const response = await request.get(url)
  return response.data
}

// 查询航线任务是否有飞行轨迹数据
export const hasTaskTrajectory = async function (jobId: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/jobs/judge-flight-path/${jobId}`
  const response = await request.get(url)
  return response.data
}

/** 根据航线id获取航线信息 */
export const waylinesFileGet = async (workspace_id:string, wayline_id:string) => {
  const url = `${HTTP_PREFIX}/workspaces/${workspace_id}/waylines/file/get/${wayline_id}`
  const response = await request.get(url)
  return response.data
}

/** 根据用户id获取航线列表 */
export const waylinesListGetByUserId = async (workspace_id:string, params) => {
  const url = `${HTTP_PREFIX}/workspaces/${workspace_id}/waylines`
  const response = await request.get(url, { params })
  return response.data
}
